import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// The advanced search criteria (XD screens 6 and 7), and their three
// representations:
//
//   - `SearchCriteria`      what the form and the results
//                           screen handle;
//   - the query string      the source of truth between screens: a link to
//                           `/search/results?…` is shareable, the back button
//                           works and "Modifier ma recherche" reopens the modal
//                           with the same fields. `parseCriteria` /
//                           `toSearchParams` go from one to the other;
//   - the API parameters (`toApiParams`), for `GET /archives/search` and
//                           `/search/count`.
//
// The query string uses the same names as the API (`q`, `issued_op`,
// `issued_from`, …) so that a URL reads the same as the call it produces,
// and so that the only difference between `toSearchParams` and `toApiParams` is
// the pagination.
//
// A date criterion only travels when it is complete (operator + date, and
// both dates with "Entre"): half-filled it is not a criterion, and sending it
// would make the backend answer 400 on each keystroke.

dayjs.extend(customParseFormat);

export type DateOperator = 'before' | 'after' | 'between';

export const DATE_OPERATORS: DateOperator[] = ['before', 'after', 'between'];

// Labels of the XD operator dropdown.
export const DATE_OPERATOR_LABELS: Record<DateOperator, string> = {
  before: 'Avant',
  after: 'Après',
  between: 'Entre',
};

export type DateCriterion = {
  op: DateOperator;
  from: Dayjs | null;
  /** Only with `between`. */
  to: Dayjs | null;
};

export type SearchCriteria = {
  keywords: string[];
  issued: DateCriterion;
  archived: DateCriterion;
  /** Service ids. */
  services: string[];
  /** User ids (admin) or free text (USER): it is what `issuer` stores. */
  issuers: string[];
  confidential: boolean;
};

// The XD draws "Avant" at rest on Emission and "Entre" on Archivage,
// but it is an artboard with example data; both start from the first
// operator in the list.
export const EMPTY_DATE: DateCriterion = { op: 'before', from: null, to: null };

export const EMPTY_CRITERIA: SearchCriteria = {
  keywords: [],
  issued: EMPTY_DATE,
  archived: EMPTY_DATE,
  services: [],
  issuers: [],
  confidential: false,
};

const API_DAY = 'YYYY-MM-DD';
export const DISPLAY_DAY = 'DD/MM/YYYY';

// "facture, 2026" → ["facture", "2026"]. It is the same split the
// backend does with `q`.
export const splitKeywords = (text: string): string[] =>
  text
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

export const joinKeywords = (keywords: string[]): string =>
  keywords.join(', ');

const splitList = (value: string | null): string[] =>
  value ? splitKeywords(value) : [];

// A complete date criterion: operator and valid date(s).
export const isDateComplete = (criterion: DateCriterion): boolean => {
  if (!criterion.from || !criterion.from.isValid()) return false;
  if (criterion.op === 'between') {
    return Boolean(criterion.to && criterion.to.isValid());
  }
  return true;
};

// Half-filled: there is something written but it does not form a criterion. It serves to
// warn under the field without bothering when it is completely empty.
export const isDatePartial = (criterion: DateCriterion): boolean =>
  !isDateComplete(criterion) &&
  (criterion.from !== null || criterion.to !== null);

const parseDay = (value: string | null): Dayjs | null => {
  if (!value) return null;
  const day = dayjs(value, API_DAY, true);
  return day.isValid() ? day : null;
};

const parseDateCriterion = (
  params: URLSearchParams,
  prefix: 'issued' | 'archived',
): DateCriterion => {
  const op = params.get(`${prefix}_op`) as DateOperator | null;
  if (!op || !DATE_OPERATORS.includes(op)) return EMPTY_DATE;

  return {
    op,
    from: parseDay(params.get(`${prefix}_from`)),
    to: op === 'between' ? parseDay(params.get(`${prefix}_to`)) : null,
  };
};

export const parseCriteria = (params: URLSearchParams): SearchCriteria => ({
  keywords: splitList(params.get('q')),
  issued: parseDateCriterion(params, 'issued'),
  archived: parseDateCriterion(params, 'archived'),
  services: splitList(params.get('services')),
  issuers: splitList(params.get('issuers')),
  confidential: params.get('confidential') === 'true',
});

const writeDateCriterion = (
  params: URLSearchParams,
  prefix: 'issued' | 'archived',
  criterion: DateCriterion,
) => {
  if (!isDateComplete(criterion)) return;

  params.set(`${prefix}_op`, criterion.op);
  params.set(`${prefix}_from`, (criterion.from as Dayjs).format(API_DAY));
  if (criterion.op === 'between') {
    params.set(`${prefix}_to`, (criterion.to as Dayjs).format(API_DAY));
  }
};

export const toSearchParams = (criteria: SearchCriteria): URLSearchParams => {
  const params = new URLSearchParams();

  if (criteria.keywords.length > 0) params.set('q', criteria.keywords.join(','));
  writeDateCriterion(params, 'issued', criteria.issued);
  writeDateCriterion(params, 'archived', criteria.archived);
  if (criteria.services.length > 0) {
    params.set('services', criteria.services.join(','));
  }
  if (criteria.issuers.length > 0) {
    params.set('issuers', criteria.issuers.join(','));
  }
  if (criteria.confidential) params.set('confidential', 'true');

  return params;
};

export type Pagination = {
  /** 1-based, like the API. */
  page: number;
  limit: number;
};

export const toApiParams = (
  criteria: SearchCriteria,
  pagination?: Pagination,
): URLSearchParams => {
  const params = toSearchParams(criteria);

  if (pagination) {
    params.set('page', String(pagination.page));
    params.set('limit', String(pagination.limit));
  }

  return params;
};

// The cache and debounce key of the counter: two forms that produce
// the same call share the same number.
export const criteriaKey = (criteria: SearchCriteria): string =>
  toSearchParams(criteria).toString();

export const isEmptyCriteria = (criteria: SearchCriteria): boolean =>
  criteriaKey(criteria) === '';

// --- Résultats chips ------------------------------------------------------

// "Emission après 10/02/2026", "Archivage 01/01/2026 - 31/03/2026". The XD
// writes "après" in lowercase after the field name.
export const describeDate = (
  field: 'Emission' | 'Archivage',
  criterion: DateCriterion,
): string => {
  const from = (criterion.from as Dayjs).format(DISPLAY_DAY);

  if (criterion.op === 'between') {
    return `${field} ${from} - ${(criterion.to as Dayjs).format(DISPLAY_DAY)}`;
  }

  return `${field} ${criterion.op === 'before' ? 'avant' : 'après'} ${from}`;
};

// One chip per applied criterion. Removing one returns the criteria without it.
export type CriterionChip = {
  key: string;
  label: string;
  remove: (criteria: SearchCriteria) => SearchCriteria;
};

type Resolvers = {
  serviceName: (id: string) => string;
  issuerName: (value: string) => string;
};

export const chipsFor = (
  criteria: SearchCriteria,
  resolvers: Resolvers,
): CriterionChip[] => {
  const chips: CriterionChip[] = [];

  criteria.keywords.forEach((keyword) => {
    chips.push({
      key: `q:${keyword}`,
      // The XD writes "Mots clés #n"; the term is more useful than its number.
      label: keyword,
      remove: (current) => ({
        ...current,
        keywords: current.keywords.filter((item) => item !== keyword),
      }),
    });
  });

  if (isDateComplete(criteria.issued)) {
    chips.push({
      key: 'issued',
      label: describeDate('Emission', criteria.issued),
      remove: (current) => ({ ...current, issued: EMPTY_DATE }),
    });
  }

  if (isDateComplete(criteria.archived)) {
    chips.push({
      key: 'archived',
      label: describeDate('Archivage', criteria.archived),
      remove: (current) => ({ ...current, archived: EMPTY_DATE }),
    });
  }

  criteria.issuers.forEach((issuer) => {
    chips.push({
      key: `issuer:${issuer}`,
      label: resolvers.issuerName(issuer),
      remove: (current) => ({
        ...current,
        issuers: current.issuers.filter((item) => item !== issuer),
      }),
    });
  });

  criteria.services.forEach((service) => {
    chips.push({
      key: `service:${service}`,
      label: resolvers.serviceName(service),
      remove: (current) => ({
        ...current,
        services: current.services.filter((item) => item !== service),
      }),
    });
  });

  if (criteria.confidential) {
    chips.push({
      key: 'confidential',
      label: 'Confidentiel',
      remove: (current) => ({ ...current, confidential: false }),
    });
  }

  return chips;
};
